import axios from '../../../lib/axios'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { profileData } from '../../auth/apis/authSlice'

// SHOW ROOM
export const showRoom = createAsyncThunk(
  'chat/showRoom',
  async (formValues, thunkAPI) => {
    const { rejectWithValue, dispatch, getState } = thunkAPI
    try {
      const state = getState()
      const { profile } = state.auth
      const { isMessageBoxOpen, isRatingVisible } = state.chat

      const page = formValues?.page || 1

      // IF WE HAVE INFORMATION FROM THE GUEST FORM
      if (!profile && formValues?.name && formValues?.mobile_number) {
        const { data } = await axios.post(`/guest/chat-room?page=${page}`, {
          name: formValues.name,
          mobile_number: `${formValues.mobile_number_code}${formValues.mobile_number}`,
        })

        const chatKey = data.room.chat_key
        localStorage.setItem('SMARTCAF_GUEST_CHATKEY', chatKey)

        dispatch(setIsGuestFormVisible(false))
        dispatch(setIsMessageBoxOpen(true))

        return data
      }

      // IF GUEST AND NOT HAVE A KEY STORED SO WE NEED TO OPEN THE GUEST FORM
      if (!profile && !localStorage.getItem('SMARTCAF_GUEST_CHATKEY')) {
        return 'NEED_GUEST_INFORMATION'
      }

      // IF GUEST AND HAVE A KEY STORED SO WE NEED TO OPEN THE CHAT BOX
      if (!profile && localStorage.getItem('SMARTCAF_GUEST_CHATKEY')) {
        const { data } = await axios.post(`/guest/chat-room?page=${page}`, {
          chat_key: localStorage.getItem('SMARTCAF_GUEST_CHATKEY'),
        })

        if (data.client.rate_last_conversation === 1) {
          dispatch(setIsRatingVisible(true))
          return data
        }
        dispatch(setIsMessageBoxOpen(true))
        return data
      }

      // IF USER AND IF RATING NEEDED
      if (profile?.rate_last_conversation === 1) {
        if (isRatingVisible) {
          dispatch(setIsRatingVisible(false))
          return
        } else {
          dispatch(setIsRatingVisible(true))
          return
        }
      }

      // IF USER
      if (profile) {
        const { data } = await axios.get(`/client/chat-room?page=${page}`)

        const chatKey = data.room.chat_key

        localStorage.setItem('SMARTCAF_CHATKEY', chatKey)

        dispatch(setIsMessageBoxOpen(true))

        return data
      }

      // IF MESSAGE BOX IS OPEN AND WE NEED TO CLOSE IT - MAY BE WE NEED TO HANDLE IT SEPERATE
      if (isMessageBoxOpen) {
        dispatch(setIsMessageBoxOpen(false))
        return
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

// SEND MESSAGE
export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (formData, thunkAPI) => {
    const { rejectWithValue, getState } = thunkAPI

    try {
      const state = getState()
      const { profile } = state.auth

      const { data } = await axios.post(
        `/${profile ? 'client' : 'guest'}/chat-room/message`,
        formData
      )
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

// RATE CHAT ROOM
export const rateChatRoom = createAsyncThunk(
  'chat/rateChatRoom',
  async (formData, thunkAPI) => {
    const { rejectWithValue, getState, dispatch } = thunkAPI

    try {
      const state = getState()
      const { profile } = state.auth

      if (!profile)
        formData.chat_key = localStorage.getItem('SMARTCAF_GUEST_CHATKEY')

      const { data } = await axios.post(
        `/${profile ? 'client' : 'guest'}/chat-room/rate`,
        formData
      )

      if (profile) dispatch(profileData())

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    isMessageBoxOpen: false,
    isGuestFormVisible: false,
    isRatingVisible: false,
    showRoomLoading: false,
    sendMessageLoading: false,
    rateChatRoomLoading: false,
    roomData: {},
    messages: [],
    itemsPerPage: 6,
    totalItems: 0,
    sentMessageSignature: 0,
    scrollToBottom: false
  },
  reducers: {
    setIsMessageBoxOpen: (state, action) => {
      state.isMessageBoxOpen = action.payload
    },
    setIsGuestFormVisible: (state, action) => {
      state.isGuestFormVisible = action.payload
    },
    setIsRatingVisible: (state, action) => {
      state.isRatingVisible = action.payload
      if (action.payload === false) {
        sessionStorage.setItem('USER_RATING_CLOSED', 'true')
      }
    },
    appendNewMessage: (state, action) => {
      const newMessage = action.payload
      let updatedMessages = state.messages.filter(
        (msg) =>
          !(
            typeof msg.id === 'string' &&
            msg.id.includes('TEMP') &&
            (msg.message === newMessage.message ||
              (!msg.message && !newMessage.message))
          )
      )
      updatedMessages.push(newMessage)
      state.messages = updatedMessages
      state.scrollToBottom = true
    },
    resetScrollToBottom: (state) => {
      state.scrollToBottom = false
    },
    setSentMessageSignature: (state, action) => {
      state.sentMessageSignature = action.payload
    },
  },
  extraReducers: (builder) => {
    // SHOW ROOM
    builder.addCase(showRoom.pending, (state) => {
      state.showRoomLoading = true
      state.error = null
    })
    builder.addCase(showRoom.fulfilled, (state, action) => {
      if (action.payload === 'NEED_GUEST_INFORMATION') {
        state.isGuestFormVisible = true
      } else {
        state.roomData = action.payload
      }

      if (action.payload?.messages) {
        state.itemsPerPage = action.payload?.messages.meta?.per_page
        state.totalItems = action.payload?.messages.meta?.total

        const returnedMessages = action.payload.messages.data.reverse()

        if (action.payload?.messages.meta?.current_page === 1) {
          state.messages = returnedMessages
        } else {
          state.messages = [...returnedMessages, ...state.messages]
        }
      }

      state.showRoomLoading = false
    })
    builder.addCase(showRoom.rejected, (state) => {
      state.showRoomLoading = false
    })

    // SEND MESSAGE
    builder.addCase(sendMessage.pending, (state) => {
      state.sendMessageLoading = true
    })
    builder.addCase(sendMessage.fulfilled, (state) => {
      state.sendMessageLoading = false
    })
    builder.addCase(sendMessage.rejected, (state) => {
      state.sendMessageLoading = false
    })

    // RATE CHAT ROOM
    builder.addCase(rateChatRoom.pending, (state) => {
      state.rateChatRoomLoading = true
    })
    builder.addCase(rateChatRoom.fulfilled, (state) => {
      state.rateChatRoomLoading = false
    })
    builder.addCase(rateChatRoom.rejected, (state) => {
      state.rateChatRoomLoading = false
    })
  },
})

export const {
  setIsMessageBoxOpen,
  setIsGuestFormVisible,
  setIsRatingVisible,
  appendNewMessage,
  resetScrollToBottom,
  setSentMessageSignature,
} = chatSlice.actions
export default chatSlice.reducer
